import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Clock, Loader2, CheckCircle, XCircle, ShieldCheck, RefreshCw, Eye } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DataTable, type DataTableColumn } from '@/components/admin/DataTable'
import { apiClient } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'

// Admin Panel Redesign — brand new section (no prior code to port). Gives
// admins a platform-wide view of AML/EDD compliance cases from the Profiler
// module's case pipeline, instead of each analyst only seeing their own
// cases via /app/profiler/cases.

// The AMLCase model (apps/api/models/profiler.py) stores status as an
// uppercase str Enum (AMLCaseStatus: PENDING/RUNNING/COMPLETE/FAILED) and
// that's what /admin/aml/cases's `status` query param expects too (it does
// AMLCaseStatus(status) server-side, an exact-value lookup). Statuses are
// normalized to this canonical uppercase form throughout so display and
// filtering stay correct regardless of the casing a given response uses.
const STATUSES = ['PENDING', 'RUNNING', 'COMPLETE', 'FAILED'] as const
type AMLStatus = (typeof STATUSES)[number]

function normalizeStatus(raw: unknown): AMLStatus | undefined {
  if (typeof raw !== 'string') return undefined
  const upper = raw.toUpperCase()
  return (STATUSES as readonly string[]).includes(upper) ? (upper as AMLStatus) : undefined
}

const STATUS_CONFIG: Record<AMLStatus, { label: string; icon: typeof Clock; badgeClass: string; iconClass: string }> = {
  PENDING: { label: 'Pending', icon: Clock, badgeClass: 'border-transparent bg-muted text-muted-foreground', iconClass: 'text-muted-foreground' },
  RUNNING: { label: 'Running', icon: Loader2, badgeClass: 'border-transparent bg-blue-500/15 text-blue-400', iconClass: 'text-blue-400' },
  COMPLETE: { label: 'Complete', icon: CheckCircle, badgeClass: 'border-transparent bg-green-500/15 text-green-400', iconClass: 'text-green-400' },
  FAILED: { label: 'Failed', icon: XCircle, badgeClass: 'border-transparent bg-red-500/15 text-red-400', iconClass: 'text-red-400' },
}

interface AMLStats {
  total: number
  by_status: Record<string, number>
}

interface AMLCaseRow {
  id?: string
  _id?: string
  case_reference?: string
  status?: string
  tier?: string
  created_at?: string
  completed_at?: string
  triggered_by?: string
  entity_id?: string
  client_ref?: string
  [key: string]: unknown
}

// by_status keys may come back as either casing depending on how the
// document's status was originally written — check both before giving up.
function statCount(byStatus: Record<string, number> | undefined, status: AMLStatus): number {
  if (!byStatus) return 0
  return byStatus[status] ?? byStatus[status.toLowerCase()] ?? 0
}

function formatDate(value?: string): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString()
}

function caseRowId(row: AMLCaseRow): string {
  return String(row.id ?? row._id ?? row.case_reference ?? Math.random())
}

export default function AMLSection() {
  const { toast } = useToast()
  const navigate = useNavigate()

  const [stats, setStats] = useState<AMLStats | null>(null)
  const [statsLoading, setStatsLoading] = useState(true)

  const [cases, setCases] = useState<AMLCaseRow[]>([])
  const [casesLoading, setCasesLoading] = useState(true)
  const [casesError, setCasesError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<AMLStatus | 'ALL'>('ALL')

  const loadStats = async () => {
    setStatsLoading(true)
    try {
      const result = await apiClient.adminGetAmlStats()
      setStats(result as AMLStats)
    } catch (e: any) {
      console.error('Failed to load AML stats', e)
      toast({ title: 'Failed to load AML stats', description: e.message, variant: 'destructive' })
    } finally {
      setStatsLoading(false)
    }
  }

  const loadCases = async (status: AMLStatus | 'ALL') => {
    setCasesLoading(true)
    setCasesError(null)
    try {
      const params = status === 'ALL' ? { limit: 100 } : { status, limit: 100 }
      const result = await apiClient.adminListAmlCases(params)
      setCases(((result as any)?.cases ?? []) as AMLCaseRow[])
    } catch (e: any) {
      console.error('Failed to load AML cases', e)
      setCasesError(e.message || 'Failed to load AML cases')
      toast({ title: 'Failed to load AML cases', description: e.message, variant: 'destructive' })
    } finally {
      setCasesLoading(false)
    }
  }

  useEffect(() => {
    loadStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadCases(statusFilter)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter])

  const refreshAll = () => {
    loadStats()
    loadCases(statusFilter)
  }

  const columns: DataTableColumn<AMLCaseRow>[] = [
    {
      key: 'case_reference',
      header: 'Case Reference',
      render: (row) => (
        <span className="font-mono text-xs">{row.case_reference || row.id || row._id || '—'}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => {
        const status = normalizeStatus(row.status)
        if (!status) return <Badge variant="outline">{String(row.status ?? 'Unknown')}</Badge>
        const cfg = STATUS_CONFIG[status]
        const Icon = cfg.icon
        return (
          <Badge className={cn('gap-1', cfg.badgeClass)}>
            <Icon className={cn('h-3 w-3', status === 'RUNNING' && 'animate-spin')} />
            {cfg.label}
          </Badge>
        )
      },
    },
    {
      key: 'created_at',
      header: 'Created At',
      render: (row) => <span className="text-xs text-muted-foreground">{formatDate(row.created_at)}</span>,
    },
    {
      key: 'triggered_by',
      header: 'Triggered By',
      render: (row) => <span className="text-sm">{row.triggered_by || '—'}</span>,
    },
  ]

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Cases</CardTitle>
            <ShieldCheck className="h-5 w-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{statsLoading ? '—' : stats?.total ?? 0}</div>
          </CardContent>
        </Card>

        {STATUSES.map((status) => {
          const cfg = STATUS_CONFIG[status]
          const Icon = cfg.icon
          return (
            <Card key={status}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{cfg.label}</CardTitle>
                <Icon className={cn('h-5 w-5', cfg.iconClass)} />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{statsLoading ? '—' : statCount(stats?.by_status, status)}</div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>AML / Compliance Cases</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Platform-wide CDD/EDD screening cases (sanctions, PEP, identity/corporate checks) across all users.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as AMLStatus | 'ALL')}
            >
              <SelectTrigger className="h-9 w-40">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All</SelectItem>
                {STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>
                    {STATUS_CONFIG[status].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={refreshAll} disabled={casesLoading || statsLoading}>
              <RefreshCw className={cn('mr-2 h-4 w-4', (casesLoading || statsLoading) && 'animate-spin')} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {casesError && <p className="mb-3 text-sm text-destructive">{casesError}</p>}

          <DataTable
            columns={columns}
            rows={cases}
            getRowId={caseRowId}
            isLoading={casesLoading}
            emptyMessage="No AML cases found."
            rowActions={[
              {
                label: 'View',
                icon: <Eye className="mr-2 h-4 w-4" />,
                onClick: (row) => {
                  const id = row.id || row._id
                  if (!id) {
                    toast({ title: 'Cannot open case', description: 'This case has no resolvable id.', variant: 'destructive' })
                    return
                  }
                  const path = String(row.tier).toUpperCase() === 'EDD' ? 'edd' : 'cdd'
                  navigate(`/app/profiler/case/${id}/${path}`)
                },
              },
            ]}
          />

          <p className="mt-3 text-xs text-muted-foreground">
            Export and purge for AML cases aren't available yet — out of scope for this pass.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
